import * as ngCore from '@angular/core'
import { Cache } from '@attain-apps/data-utils'
import * as ents from '@attain-apps/entities'
import * as clipboard from '@attain-apps/fe/web/main/lib/clipboard'
import * as ec from '@attain-apps/fe/web/main/lib/entity-collection'
import * as excel from '@attain-apps/fe/web/main/lib/excel'
import { TypeChecker } from '@attain-apps/type-guards'
import { ComponentStore } from '@ngrx/component-store'

import * as data from './data'
import * as plugin from './ec-plugin'
import * as models from './models'
import { AtnDsTableState } from './state'

const ICON_WITH_PADDING_WIDTH = 34 // Icon is 24px wide + 5px padding on both sides (2 × 5px)
const ICONS_RENDERER_PADDING_WIDTH = 10 // 2 * 5px
/** TODO: explain */
@ngCore.Injectable()
export class AtnDsTableStore<
  TTypeParams extends ec.models.TypeParams.Constraint,
> extends ComponentStore<AtnDsTableState<TTypeParams>> {
  /** @see {@link ec.Collection}*/
  public readonly ecSg = this.selectSignal(state => state.ec)
  /** @see {@link plugin.Plugin<TTypeParams>} */
  public readonly pluginSg = ngCore.computed((_ec = this.ecSg()) =>
    _ec?.plugins.lookup(plugin.Plugin<TTypeParams>).get(),
  )
  /** @see {@link plugin.models.Config} */
  public readonly pluginConfigSg = ngCore.computed(
    (_plugin = this.pluginSg()) => _plugin?.config,
  )
  /** @see {@link plugin.models.Config.rowConfig} */
  private readonly _rowConfigSg = ngCore.computed(
    (config = this.pluginConfigSg()) => config?.rowConfig,
  )
  /** @see {@link excel.ec.Plugin} */
  public readonly excelPluginSg = ngCore.computed((_ec = this.ecSg()) =>
    _ec?.plugins.lookup(excel.ec.Plugin<TTypeParams>).tryGet(),
  )
  /** @see {@link clipboard.Plugin} */
  public readonly clipboardPluginSg = ngCore.computed((_ec = this.ecSg()) =>
    _ec?.plugins.lookup(clipboard.Plugin<TTypeParams>).tryGet(),
  )
  /** @see {@link clipboardPluginSg} */
  public readonly hasClipboardPluginSg = ngCore.computed(
    () => this.clipboardPluginSg() !== undefined,
  )
  /** @see {@link excelPluginSg} */
  public readonly hasExcelPluginSg = ngCore.computed(
    () => this.excelPluginSg() !== undefined,
  )
  /** @see {@link AtnDsTableState.showPagination} */
  public readonly showPaginationSg = this.selectSignal(
    state => state.showPagination,
  )

  /**
   * Computes the maximum width for the icons column, accounting for the maximum
   * number of icons across all rows, as well as additional padding.
   *
   * The width is calculated by finding the row with the most icons, multiplying
   * that count by a predefined {@link ICON_WITH_PADDING_WIDTH width}, and
   * adding {@link ICONS_RENDERER_PADDING_WIDTH padding} if applicable.
   */
  public readonly iconsMaxWidthSg = ngCore.computed(
    (iconsMaxCount = this._iconsMaxCountSg()) => {
      if (iconsMaxCount === 0) {
        return iconsMaxCount
      }
      const iconsWidth = iconsMaxCount * ICON_WITH_PADDING_WIDTH
      return iconsWidth + ICONS_RENDERER_PADDING_WIDTH
    },
  )

  /** @see {@link AtnDsTableState.tableWidth} */
  public readonly tableWidthSg = this.selectSignal(state => state.tableWidth)

  /** @see {@link AtnDsTableState.horizontalScrollLeftOffsetPosition} */
  public readonly horizontalScrollLeftOffsetPositionSg = this.selectSignal(
    state => state.horizontalScrollLeftOffsetPosition,
  )

  /** @see {@link AtnDsTableState.creation} */
  public readonly creationSg = this.selectSignal(state => state.creation)

  /** @see {@link plugin.models.Config.search} */
  public hasSearchSg = ngCore.computed((config = this.pluginConfigSg()) =>
    TypeChecker.isNotFalsish(config?.search),
  )
  /** @see {@link AtnDsTableState.isCellContentWrapped} */
  public readonly isCellContentWrappedSg = this.selectSignal(
    state => state.isCellContentWrapped,
  )
  /** @see {@link AtnDsTableState.isEmbedded} */
  public readonly isEmbeddedSg = this.selectSignal(state => !!state.isEmbedded)
  /** @see {@link AtnDsTableState.columnConfigs} */
  public readonly columnConfigsSg = this.selectSignal(
    state => state.columnConfigs,
  )
  /** @see {@link AtnDsTableState.columnWidths} */
  public readonly columnWidthsSg = this.selectSignal(
    state => state.columnWidths,
  )
  /** @see {@link AtnDsTableState.columnMaxWidths} */
  public readonly columnMaxWidthsSg = this.selectSignal(
    state => state.columnMaxWidths,
  )
  /** @see {@link AtnDsTableState.columnSettingsIsOpen} */
  public readonly columnSettingsIsOpenSg = this.selectSignal(
    state => state.columnSettingsIsOpen,
  )
  /** @see {@link AtnDsTableState.showColumnSettings} */
  public readonly showColumnSettingsSg = this.selectSignal(
    state => state.showColumnSettings,
  )
  /** @see {@link AtnDsTableState.showRowSelect} */
  public readonly showRowSelectSg = this.selectSignal(
    state => state.showRowSelect,
  )
  /** @see {@link AtnDsTableState.selectionTarget} */
  public readonly selectionTargetSg = this.selectSignal(
    state => state.selectionTarget,
  )
  /** @see {@link AtnDsTableState.name} */
  public readonly nameSg = this.selectSignal(state => state.name)
  /**
   * All entities loaded in {@link ec.plugins.standard.currentList.Plugin}.
   */
  private readonly _currentEntitiesSg = ngCore.computed(
    (_plugin = this.pluginSg()) =>
      _plugin?.collection.plugins.currentListPlugin.feature.selectors.entities.asSignal() ??
      [],
  )
  /** TODO: */
  public readonly visibleAttributesSg = ngCore.computed(
    (collection = this.ecSg()) =>
      collection?.plugins.attributePlugin.feature.selectors.visibleAttributes.asSignal() ??
      [],
  )

  /** Computes a list of {@link models.config.Row.Applied Row.Applied} objects without the `cells` property. */
  private readonly _partialRowsSg = ngCore.computed(
    (
      /** @see {@link _currentEntitiesSg} */
      entities = this._currentEntitiesSg(),
      /** @see {@link _rowConfigSg} */
      rowConfigFactory = this._rowConfigSg(),
    ): Omit<models.config.Row.Applied<TTypeParams>, 'cells'>[] =>
      entities.map(
        (
          entity,
          rowIdx,
        ): Omit<models.config.Row.Applied<TTypeParams>, 'cells'> => {
          /** @see {@link models.config.Row} */
          let rowConfig: models.config.Row = {}

          /** @see {@link models.config.Row.Context<TTypeParams>} */
          const rowContext: models.config.Row.Context<TTypeParams> = {
            entity,
            rowIdx,
          }

          // if 'rowConfigFactory' is provided, apply it to the row.
          // if not use only 'rowContext'
          if (rowConfigFactory) {
            rowConfig = rowConfigFactory({ entity, rowIdx })
          }

          return {
            ...rowConfig,
            ...rowContext,
          }
        },
      ),
  )

  /**
   * Indicates whether at least one {@link models.config.Row.Applied row}
   * contains {@link models.config.Row.iconsConfigs icons}.
   */
  public readonly hasIconsSg = ngCore.computed((rows = this._partialRowsSg()) =>
    rows.some(row => row.iconsConfigs?.length ?? 0 > 0),
  )

  /**
   * Indicates the maximum number of
   * {@link models.config.Row.iconsConfigs icons} present in any single
   * {@link models.config.Row.Applied row}.
   */
  private readonly _iconsMaxCountSg = ngCore.computed(
    (rows = this._partialRowsSg()) =>
      rows.reduce(
        (maxCount, row) => Math.max(maxCount, row.iconsConfigs?.length ?? 0),
        0,
      ),
  )

  /**
   * Rows for the table.
   *
   * @see {@link models.config.Row.Applied}
   */
  public readonly rowsSg = ngCore.computed(
    (
      rows = this._partialRowsSg(),
      attributes = this.visibleAttributesSg(),
      columnConfigs = this.columnConfigsSg(),
    ): models.config.Row.Applied<TTypeParams>[] =>
      rows.map(
        (row, rowIdx): models.config.Row.Applied<TTypeParams> => ({
          ...row,
          cells: attributes.map(
            (attribute): models.config.Cell.Applied<TTypeParams> => {
              /** Column configuration for the attribute (id) */
              const columnConfig =
                columnConfigs[attribute.id] ?? models.config.Column.DEFAULT
              /**
               * Extracted cell value using the {@link ents.core.common.Attribute.ValueExtractor value extractor}.
               */
              const value = attribute.getValueFrom(row.entity)

              /** @see {@link models.config.Cell.Context} */
              const cellContext: models.config.Cell.Context<TTypeParams> = {
                attribute,
                entity: row.entity,
                rowIdx,
                value,
              }

              // apply cellConfigFactory
              const { component, ...restCellConfig } = {
                ...models.config.Cell.Factory.DEFAULT(cellContext),
                ...columnConfig.cellConfig(cellContext),
              }

              return {
                ...cellContext,
                ...restCellConfig,
                // TODO: work on default component if non is passed.
                component: component ?? data.cell.value.Component,
              }
            },
          ),
        }),
      ),
  )

  /** TODO:  */
  public readonly allRowsSelectedSg = ngCore.computed(
    (
      _ec = this.ecSg(),
      entities = this._currentEntitiesSg(),
      selectionTarget = this.selectionTargetSg(),
    ) =>
      _ec?.plugins.selectionPlugin.feature.selectors
        // NOTE undefined entities can't be selected.
        .isSelected({
          entities,
          target: selectionTarget,
        })
        .asSignal() ?? false, // If entity collection doesn't exist, return false
  )
  /** TODO:  */
  public readonly someRowsSelectedSg = ngCore.computed(
    (
      _ec = this.ecSg(),
      entities = this._currentEntitiesSg(),
      selectionTarget = this.selectionTargetSg(),
    ) =>
      _ec?.plugins.selectionPlugin.feature.selectors
        // NOTE undefined entities can't be selected.
        .isSelected(
          {
            entities,
            target: selectionTarget,
          },
          'some',
        )
        .asSignal() ?? false, // If entity collection doesn't exist, return false
  )

  /** TODO:  */
  public readonly hoveredRowSg = this.selectSignal(state => state.hoveredRow)
  /** TODO:  */
  public readonly hoveredRowElSg = ngCore.computed(
    (hoveredRow = this.hoveredRowSg()) => hoveredRow?.el,
  )
  /** TODO:  */
  public readonly hoveredRowRefSg = ngCore.computed(
    (hoveredRow = this.hoveredRowSg()) => hoveredRow?.rowRef,
  )

  /**
   * Gets column {@link models.config.Column configuration} for given column id.
   */
  public getColumnConfigSg = Cache.fn(
    (id: string): ngCore.Signal<models.config.Column<TTypeParams>> =>
      ngCore.computed(
        (
          columnConfigs = this.columnConfigsSg(),
          collection = this.ecSg(),
          attribute = collection?.plugins.attributePlugin.feature.selectors
            .getAttribute(id)
            .asSignal(),
        ) => models.config.Column.from(columnConfigs[id], attribute),
      ),
  )
  /**
   * Gets current column width for given column id.
   */
  public getColumnWidthSg = Cache.fn(
    (id: string): ngCore.Signal<number | undefined> =>
      ngCore.computed(
        (columnWidths = this.columnWidthsSg()) => columnWidths[id],
      ),
  )
  /**
   * Gets max column width for given column id.
   */
  public getMaxColumnWidthSg = Cache.fn(
    (id: string): ngCore.Signal<number | undefined> =>
      ngCore.computed(
        (columnMaxWidths = this.columnMaxWidthsSg()) => columnMaxWidths[id],
      ),
  )

  constructor() {
    super(AtnDsTableState.INITIAL)

    /**
     * Whenever the entity collection visible attribute change update column configs.
     */
    ngCore.effect(() => {
      const _plugin = this.pluginSg()

      if (_plugin) {
        const visibleAttributes = this.visibleAttributesSg()
        visibleAttributes.forEach(attribute => {
          this.patchState(state => {
            /** Provided column config */
            const customColumnConfig =
              _plugin.config.columnConfigs[attribute.id]

            state.columnConfigs[attribute.id] = customColumnConfig
              ? models.config.Column.from(customColumnConfig)
              : _plugin.config.cellConfig
                ? models.config.Column.from(
                    {
                      cellConfig: ctx =>
                        _plugin.config.cellConfig!(ctx) ??
                        models.config.Cell.Factory.DEFAULT(ctx),
                    },
                    attribute,
                  )
                : models.config.Column.DEFAULT

            return state
          })
        })
      }
    })
  }

  /**
   * Create callback that dispatch edit action with data passed from component
   * that is using table
   */
  public create = (_data: TTypeParams['createEntity']) => {
    this.ecSg()
      ?.plugins.editingPlugin.feature.actions.edit.from(
        'TableComponent.Store.CreationEntity',
      )
      .dispatch({
        edit: { input: _data },
        id: ec.plugins.standard.editing.models.CreationId.generate(),
      })
  }

  /**
   * Handling creation entity click, in case of
   * - creation is function, will call creation callback
   * - creation mode is true, it will dispatch edit action
   */
  public creationEntityHandling() {
    const creation = this.creationSg()

    if (TypeChecker.isFunction(creation)) {
      creation(this.create)
    }

    if (creation === true) {
      this.ecSg()
        ?.plugins.editingPlugin.feature.actions.edit.from(
          'TableComponent.Store.CreationEntity',
        )
        .dispatch({
          id: ec.plugins.standard.editing.models.CreationId.generate(),
        })
    }
  }

  /**
   * Updates current column width for the given column id.
   *
   * Supports either setting new width, or passing function for updating current
   * width.
   *
   * @see {@link AtnDsTableState.columnWidths}
   */
  public updateColumnWidth(
    id: string,
    width: number | ((currentWidth: number | undefined) => number),
  ): void {
    this.patchState(state => ({
      ...state,
      columnWidths: {
        ...state.columnWidths,
        [id]: typeof width === 'number' ? width : width(state.columnWidths[id]),
      },
    }))
  }
  /**
   * TODO
   *
   * @see {@link AtnDsTableState.columnMaxWidths}
   */
  public updateMaxColumnWidth(
    id: ents.core.common.Attribute.Id,
    newWidth: number,
  ): void {
    const oldWidth = this.getMaxColumnWidthSg(id)()
    if (oldWidth === undefined || newWidth > oldWidth) {
      this.patchState(state => ({
        ...state,
        columnMaxWidths: {
          ...state.columnMaxWidths,
          [id]: newWidth,
        },
      }))
    }
  }

  /** TODO:  */
  public allRowSelectionToggle() {
    const collection = this.ecSg()
    if (!collection) {
      return
    }
    const selectionTarget = this.selectionTargetSg()
    collection.plugins.selectionPlugin.applied.actions.putInSelectionLookup
      .from('Table.ColumnHeaders.AllRowSelectionToggle.Clicked')
      .dispatch({
        key: selectionTarget,
        value: this.allRowsSelectedSg()
          ? { release: true }
          : {
              set: collection.plugins.currentListPlugin.feature.selectors.ids.asSignal(),
            },
      })
  }

  /** TODO:  */
  public changeRowSelection({
    entity,
    isSelected,
  }: Readonly<{
    entity: TTypeParams['entity']
    isSelected: boolean
  }>) {
    const collection = this.ecSg()
    if (collection) {
      const entityId = collection.identify(entity)
      const selectionTarget = this.selectionTargetSg()
      collection.plugins.selectionPlugin.applied.actions.putInSelectionLookup
        .from(`Table.Row.${entityId}.Selection.Changed`)
        .dispatch(
          isSelected
            ? { key: selectionTarget, value: { add: [entityId] } }
            : { key: selectionTarget, value: { remove: [entityId] } },
        )
    }
  }

  /** TODO:  */
  public setHoveredRow(hoveredRow: AtnDsTableState<TTypeParams>['hoveredRow']) {
    this.patchState({ hoveredRow })
  }
}
